// B-136 — the committee's presentation for the client, as a PowerPoint laid out like the operator's
// own monthly deck ("Presentación Seguimiento", `Set up office/Bancamia/DashBoard`): its cover, its
// road-shaped agenda, its section slides with their photographs and yellow icons, its content slides
// (the blue band on top, the blue title, the logo, the yellow rule and the confidentiality footer),
// and the same order — transactions and amounts, excesses, reversals, PQRs, commitments, thanks.
// What the deck pasted as pictures of Excel is drawn here as native charts and tables over the deck's
// own backgrounds, every figure written on them, so the client can read and edit each one.
// The images are the deck's own (`src/Assets/comite/`); PQRs and commitments are not in Bantotal, so
// their slides are left ready to be filled in the committee.
//
// `presentacionDelComite(PptxGenJS, reporte, opciones)` is pure but for pptxgenjs, so a test can hand
// it a recording stand-in and check every slide without writing a file.

import portada from "../Assets/comite/portada.jpg";
import agenda from "../Assets/comite/agenda.jpg";
import seccionTransacciones from "../Assets/comite/seccion-transacciones.jpg";
import seccionExcesos from "../Assets/comite/seccion-excesos.jpg";
import seccionReversos from "../Assets/comite/seccion-reversos.jpg";
import seccionPqrs from "../Assets/comite/seccion-pqrs.png";
import iconoExcesos from "../Assets/comite/icono-excesos.png";
import iconoReversos from "../Assets/comite/icono-reversos.png";
import iconoPqrs from "../Assets/comite/icono-pqrs.png";
import fondoGrafica from "../Assets/comite/fondo-grafica.jpg";
import fondoCurva from "../Assets/comite/fondo-curva.jpg";
import gracias from "../Assets/comite/gracias.jpg";
import degradado from "../Assets/comite/degradado.jpg";
import bandaSuperior from "../Assets/comite/banda-superior.png";
import logoBrinksAzul from "../Assets/comite/logo-brinks-azul.png";
import logoBrinksBlanco from "../Assets/comite/logo-brinks-blanco.png";
import logoEpago from "../Assets/comite/logo-epago.png";
import {
  etiquetaDePeriodo,
  indicadoresDelMes,
  numero,
  oficinasPorVolumen,
  porcentaje,
  ranking,
  tendenciaDiaria,
  transaccionesPorAnio,
  variacion,
} from "./cierreComite";
import { formatMoney } from "./formatMoney";

/** The deck's images, as the build serves them. `descargarPresentacion` hands their data in. */
export const IMAGENES = {
  portada,
  agenda,
  seccionTransacciones,
  seccionExcesos,
  seccionReversos,
  seccionPqrs,
  iconoExcesos,
  iconoReversos,
  iconoPqrs,
  fondoGrafica,
  fondoCurva,
  gracias,
  degradado,
  bandaSuperior,
  logoBrinksAzul,
  logoBrinksBlanco,
  logoEpago,
};

// The deck's palette, read off its slides.
const AZUL = "1A55B5";
const AZUL_OSCURO = "1F3864";
const AZUL_SERIE = "2E5FA8";
const AZUL_CLARO = "9DC3E6";
const AMARILLO = "F5B800";
const VERDE_BARRA = "8DB43E";
const TINTA = "1F2937";
const GRIS = "6B7280";
const LINEA = "D9D9D9";
const FONDO_FILA = "F2F2F2";
const FUENTE = "Arial";
const SEMAFORO = {
  negro: { color: "1A1A1A", texto: "Decremento superior al 10%" },
  amarillo: { color: "F2C14E", texto: "Incremento inferior al 10%" },
  verde: { color: "5DAE4F", texto: "Incremento del 10% o superior" },
  rojo: { color: "E46B6B", texto: "Decremento no superior al 10%" },
};

// The deck is 960 × 540 points; pptxgenjs's wide layout is 13,33 × 7,5 inches: 72 points an inch.
const p = (puntos) => puntos / 72;
const cuenta = (n) => formatMoney(Math.round(numero(n)));
const pesos = (n) => `$ ${formatMoney(Math.round(numero(n)))}`;
const millones = (n) => Math.round(numero(n) / 1e6);
const mayuscula = (t) => (t ? t.charAt(0).toUpperCase() + t.slice(1) : t);

/** "VILLETA - CORRESPONSAL EPAGO" as the deck writes it: "Villeta" — no tail, in title case. */
export function nombreDeOficina(nombre) {
  const corto = String(nombre || "")
    .replace(/\s*-?\s*(CORRESPONSAL|CORR\.|C\.EPAGO|EPAGO).*$/i, "")
    .replace(/^BANCAMIA\s+/i, "")
    .trim();
  return corto.toLowerCase()
    .replace(/(^|[\s(.-])([a-záéíóúñü])/g, (m, a, b) => a + b.toUpperCase())
    // "PE" is Punto Express, said in capitals as the operator does.
    .replace(/^Pe\s/, "PE ");
}

/** The traffic light of a variation, as the deck's own legend says it. */
export function semaforo(v) {
  if (v === null || v === undefined) return null;
  if (v <= -0.1) return SEMAFORO.negro;
  if (v < 0) return SEMAFORO.rojo;
  if (v < 0.1) return SEMAFORO.amarillo;
  return SEMAFORO.verde;
}

function pie(slide, anio, color = "595959") {
  slide.addShape("rect", { x: p(27), y: p(498), w: p(28), h: p(28), fill: { color: "FFFFFF" }, line: { color: "7F7F7F", width: 0.75 } });
  slide.addText(`©${anio} BRINK'S. CONFIDENTIAL. FOR INTERNAL USE ONLY – DO NOT DUPLICATE OR DISTRIBUTE.`, {
    x: p(62), y: p(505), w: p(520), h: p(16), fontFace: FUENTE, fontSize: 7, color,
  });
}

const reglaAmarilla = (slide, x = 0, w = 960) => slide.addShape("rect", {
  x: p(x), y: p(535), w: p(w), h: p(5), fill: { color: AMARILLO }, line: { color: AMARILLO, width: 0 },
});

/**
 * The deck, for one report.
 *
 * @param {Function} PptxGenJS  the pptxgenjs constructor
 * @param {object} reporte      `getCierreOperationReport`'s data
 * @param {{ cliente: string, filtrar?: Function, imagenes?: object }} opciones
 */
export function presentacionDelComite(PptxGenJS, reporte, { cliente, filtrar = (filas) => filas, imagenes = {} }) {
  const I = { ...IMAGENES, ...imagenes };
  const visibles = { ...reporte, offices: filtrar(reporte.offices || []) };
  const pptx = new PptxGenJS();
  pptx.layout = "LAYOUT_WIDE";
  pptx.title = `Comité de seguimiento ${cliente} ${etiquetaDePeriodo(reporte)}`;
  pptx.company = cliente;
  const anio = new Date().getFullYear();
  const mes = mayuscula(etiquetaDePeriodo(reporte));
  const actCorto = mayuscula(etiquetaDePeriodo(reporte, "actual", { corta: true }));
  const tope = Math.round((numero(reporte.overLimitFactor) - 1) * 100);

  // A content slide: the blue band, the title, the logo, the yellow rule, the footer.
  const contenido = (titulo, { subtitulo } = {}) => {
    const slide = pptx.addSlide();
    slide.background = { color: "FFFFFF" };
    slide.addImage({ path: I.bandaSuperior, x: 0, y: 0, w: p(960), h: p(11) });
    slide.addText(titulo, { x: p(34), y: p(18), w: p(720), h: p(50), fontFace: FUENTE, fontSize: 30, color: AZUL });
    if (subtitulo) slide.addText(subtitulo, { x: p(36), y: p(64), w: p(720), h: p(22), fontFace: FUENTE, fontSize: 12, color: GRIS });
    slide.addImage({ path: I.logoBrinksAzul, x: p(792), y: p(38), w: p(113), h: p(19.5) });
    reglaAmarilla(slide);
    pie(slide, anio);
    return slide;
  };

  // A section slide: the photograph on one side, the section's name and its yellow icon on the other.
  const seccion = ({ titulo, foto, icono, fotoALaDerecha = true }) => {
    const slide = pptx.addSlide();
    slide.background = { color: "FCFCFC" };
    slide.addImage({ path: foto, x: fotoALaDerecha ? p(526) : 0, y: 0, w: p(434), h: p(540), sizing: { type: "cover", w: p(434), h: p(540) } });
    slide.addImage({ path: I.bandaSuperior, x: fotoALaDerecha ? 0 : p(434), y: 0, w: p(526), h: p(11) });
    if (!fotoALaDerecha) slide.addImage({ path: I.logoBrinksAzul, x: p(792), y: p(38), w: p(113), h: p(19.5) });
    slide.addText(titulo, { x: fotoALaDerecha ? p(36) : p(530), y: fotoALaDerecha ? p(70) : p(135), w: p(400), h: p(50), fontFace: FUENTE, fontSize: 26, color: AZUL });
    slide.addImage({ path: icono, x: fotoALaDerecha ? p(150) : p(620), y: p(185), w: p(200), h: p(200), sizing: { type: "contain", w: p(200), h: p(200) } });
    reglaAmarilla(slide, fotoALaDerecha ? 0 : 434, 526);
    if (fotoALaDerecha) pie(slide, anio);
    return slide;
  };

  // The deck's chart frame: the grey "Juntos somos mejores" canvas (its ePago logo in the corner), or
  // the white one with ePago's blue curve on top.
  const marcoDeGrafica = (slide, { x, y, w, h, curva = false }) => {
    slide.addImage({ path: curva ? I.fondoCurva : I.fondoGrafica, x, y, w, h: curva ? p(90) : h, sizing: { type: "cover", w, h: curva ? p(90) : h } });
    slide.addShape("rect", { x, y, w, h, fill: { type: "none" }, line: { color: LINEA, width: 0.75 } });
  };

  // Tight cell margins (points): PowerPoint's own would make every row twice the height asked.
  const celda = (text, extra = {}) => ({ text: String(text), options: { fontFace: FUENTE, fontSize: 7.5, color: TINTA, valign: "middle", margin: [1, 4, 1, 4], ...extra } });
  const encabezado = (text, align = "center") => celda(text, { bold: true, color: "FFFFFF", fill: { color: AZUL_OSCURO }, align });

  // "Volumen / Montos por oficina": the deck's two tables side by side, the traffic light beside each variation.
  const tablaPorOficina = (filas, ant, act, formato) => [
    [encabezado("Oficina", "left"), encabezado(ant, "right"), encabezado(act, "right"), encabezado(""), encabezado("Variación", "right")],
    ...filas.map((o, i) => {
      const fondo = i % 2 ? { fill: { color: FONDO_FILA } } : {};
      const luz = semaforo(o.variacion);
      return [
        celda(nombreDeOficina(o.oficina), fondo),
        celda(formato(o.anterior), { ...fondo, align: "right" }),
        celda(formato(o.actual), { ...fondo, align: "right" }),
        celda(luz ? "●" : "", { ...fondo, align: "center", color: luz ? luz.color : TINTA, fontSize: 9 }),
        celda(porcentaje(o.variacion), { ...fondo, align: "right" }),
      ];
    }),
  ];

  const leyendaSemaforo = (slide, x, y) => {
    Object.values(SEMAFORO).forEach((luz, i) => {
      slide.addShape("ellipse", { x, y: y + i * p(22), w: p(12), h: p(12), fill: { color: luz.color }, line: { color: "FFFFFF", width: 0.5 } });
      slide.addText(luz.texto, { x: x + p(18), y: y + i * p(22) - p(3), w: p(150), h: p(18), fontFace: FUENTE, fontSize: 8, italic: true, color: TINTA });
    });
  };

  const porOficina = (campo, titulo, formato) => {
    const filas = oficinasPorVolumen(visibles, campo);
    const ant = mayuscula(etiquetaDePeriodo(reporte, "anterior", { corta: true }));
    const porTabla = 30;
    for (let i = 0; i < Math.max(1, filas.length); i += porTabla * 2) {
      const slide = contenido(titulo);
      [filas.slice(i, i + porTabla), filas.slice(i + porTabla, i + porTabla * 2)].forEach((parte, j) => {
        if (!parte.length) return;
        slide.addTable(tablaPorOficina(parte, ant, actCorto, formato), {
          x: p(j ? 402 : 14), y: p(72), w: p(376), colW: [p(124), p(88), p(88), p(18), p(58)], rowH: p(12.4), border: { type: "solid", color: LINEA, pt: 0.5 },
        });
      });
      leyendaSemaforo(slide, p(790), p(205));
    }
  };

  const filasDeRanking = (datos, columnaCantidad, conRegional) => [
    [...(conRegional ? [encabezado("Regional")] : []), encabezado("Oficina"), encabezado("Código"), encabezado(columnaCantidad), encabezado("Valor")],
    ...datos.filas.map((o, i) => {
      const fondo = i % 2 ? { fill: { color: FONDO_FILA } } : {};
      return [
        ...(conRegional ? [celda(o.region || "—", { ...fondo, color: AZUL_SERIE })] : []),
        celda(nombreDeOficina(o.oficina), { ...fondo, color: AZUL_SERIE }),
        celda(o.codigo, { ...fondo, align: "center" }),
        celda(cuenta(o.cantidad), { ...fondo, align: "center" }),
        celda(pesos(o.valor), { ...fondo, align: "right" }),
      ];
    }),
    [
      celda("Total", { bold: true, color: "FFFFFF", fill: { color: AZUL_OSCURO } }),
      ...(conRegional ? [celda("", { fill: { color: AZUL_OSCURO } })] : []),
      celda("", { fill: { color: AZUL_OSCURO } }),
      celda(cuenta(datos.total.cantidad), { bold: true, color: "FFFFFF", fill: { color: AZUL_OSCURO }, align: "center" }),
      celda(pesos(datos.total.valor), { bold: true, color: "FFFFFF", fill: { color: AZUL_OSCURO }, align: "right" }),
    ],
  ];

  // Two rankings, the period before and this one, each under its own banner, as the deck lays them out.
  const rankingsLadoALado = (slide, { cantidad, valor, columnaCantidad, titulo, limite = 10, y = 300, conRegional = true }) => {
    ["anterior", "actual"].forEach((periodo, j) => {
      const datos = ranking(visibles, periodo, cantidad, valor, limite);
      const x = p(j ? 470 : 14);
      const w = p(j ? 466 : 436);
      slide.addText(`${titulo} ${mayuscula(etiquetaDePeriodo(reporte, periodo))}`, {
        x, y: p(y), w, h: p(16), fontFace: FUENTE, fontSize: 9, color: "FFFFFF", fill: { color: AZUL_OSCURO }, align: "center",
      });
      if (datos.filas.length) {
        const anchos = conRegional ? [0.2, 0.32, 0.12, 0.13, 0.23] : [0.42, 0.15, 0.18, 0.25];
        slide.addTable(filasDeRanking(datos, columnaCantidad, conRegional), {
          x, y: p(y + 16), w, colW: anchos.map((a) => w * a), rowH: p(13.5), border: { type: "solid", color: "BFBFBF", pt: 0.5 },
        });
      } else {
        slide.addText("Sin casos en el periodo.", { x, y: p(y + 22), w, h: p(20), fontFace: FUENTE, fontSize: 10, color: GRIS, align: "center" });
      }
    });
  };

  // "Comparativo <año>": the year's months as green bars with their figure on each, the ePago logo in
  // the frame's corner, and the line that says this month against the one before.
  const nombresDeMes = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
  const comparativo = (slide, clave, nombre, { x, y, w, h }) => {
    const anioDelReporte = String(reporte.month).slice(0, 4);
    const meses = (reporte.monthly || []).filter((m) => m.month.startsWith(anioDelReporte));
    marcoDeGrafica(slide, { x, y, w, h });
    slide.addText(`Comparativo ${anioDelReporte}`, { x: x + p(16), y: y + p(8), w: p(260), h: p(26), fontFace: FUENTE, fontSize: 16, bold: true, color: AZUL_OSCURO });
    const actual = meses.find((m) => m.month === reporte.month);
    const previo = meses.find((m) => m.month === reporte.previousMonth);
    if (actual && previo && reporte.wholeMonth !== false) {
      const diff = numero(actual[clave]) - numero(previo[clave]);
      slide.addText(
        `${actCorto} vs ${mayuscula(etiquetaDePeriodo(reporte, "anterior", { corta: true }))}\n${porcentaje(variacion(actual[clave], previo[clave]))} / ${cuenta(Math.abs(diff))} ${nombre} ${diff <= 0 ? "menos" : "más"}`,
        { x: x + w / 2 - p(110), y: y + p(6), w: p(220), h: p(32), fontFace: FUENTE, fontSize: 9, bold: true, color: TINTA, align: "center" }
      );
    }
    if (meses.length) {
      slide.addChart(pptx.ChartType.bar, [{ name: anioDelReporte, labels: meses.map((m) => nombresDeMes[Number(m.month.slice(5)) - 1]), values: meses.map((m) => numero(m[clave])) }], {
        x: x + p(10), y: y + p(38), w: w - p(20), h: h - p(46), barDir: "col", chartColors: [VERDE_BARRA], barGapWidthPct: 60,
        showValue: true, dataLabelPosition: "outEnd", dataLabelFontSize: 10, dataLabelColor: TINTA, dataLabelFormatCode: "#,##0",
        valAxisLabelFontSize: 8, catAxisLabelFontSize: 9, valGridLine: { style: "none" }, valAxisLabelFormatCode: "#,##0", showLegend: false,
      });
    } else {
      slide.addText("Sin meses con cierres este año.", { x, y: y + h / 2, w, h: p(20), fontFace: FUENTE, fontSize: 11, color: GRIS, align: "center" });
    }
  };

  // 1. The cover.
  const tapa = pptx.addSlide();
  tapa.addImage({ path: I.portada, x: 0, y: 0, w: p(960), h: p(540), sizing: { type: "cover", w: p(960), h: p(540) } });
  tapa.addImage({ path: I.logoBrinksBlanco, x: p(28), y: p(34), w: p(112), h: p(19.5) });
  tapa.addText("COMITÉ DE SEGUIMIENTO", { x: p(370), y: p(30), w: p(570), h: p(52), fontFace: FUENTE, fontSize: 32, color: "FFFFFF" });
  tapa.addShape("line", { x: p(372), y: p(80), w: p(560), h: 0, line: { color: AMARILLO, width: 2 } });
  tapa.addText(`Indicador ${cliente} ${mes}`, { x: p(372), y: p(88), w: p(560), h: p(28), fontFace: FUENTE, fontSize: 16, color: "FFFFFF" });
  pie(tapa, anio, "E5E7EB");

  // 2. The agenda, on the deck's road.
  const ag = pptx.addSlide();
  ag.addImage({ path: I.agenda, x: 0, y: 0, w: p(960), h: p(540), sizing: { type: "cover", w: p(960), h: p(540) } });
  ag.addText("AGENDA", { x: p(330), y: p(22), w: p(300), h: p(46), fontFace: FUENTE, fontSize: 30, color: "1F4E9C", align: "center" });
  ag.addImage({ path: I.logoBrinksAzul, x: p(792), y: p(38), w: p(113), h: p(19.5) });
  [
    { n: "01", texto: "Indicador de\ntransacciones\ny montos procesados", x: 40, y: 36, color: "E0A21B", ancho: 150 },
    { n: "02", texto: "Excesos", x: 215, y: 110, color: "D9661F", ancho: 110 },
    { n: "03", texto: "Reversos", x: 405, y: 144, color: "93278F", ancho: 115 },
    { n: "04", texto: "Indicador de\nPQR´S", x: 615, y: 96, color: "7A4EA8", ancho: 125 },
    { n: "05", texto: "Seguimiento a\ncompromisos", x: 790, y: 256, color: "1B9AD6", ancho: 130 },
  ].forEach((punto) => {
    // The road picture already carries the numbers 01–05; only the words go over it.
    ag.addText(punto.texto, {
      x: p(punto.x - 10), y: p(punto.y + 32), w: p(punto.ancho + 20), h: p(52), fontFace: FUENTE, fontSize: 11, bold: true, color: punto.color, align: "center", valign: "top",
    });
  });
  ag.addShape("rect", { x: p(462), y: p(482), w: p(18), h: p(58), fill: { color: AMARILLO }, line: { color: AMARILLO, width: 0 } });
  pie(ag, anio);

  // 3. Section: transactions and amounts.
  const s1 = pptx.addSlide();
  s1.background = { color: "000000" };
  s1.addImage({ path: I.seccionTransacciones, x: p(560), y: 0, w: p(400), h: p(540), sizing: { type: "cover", w: p(400), h: p(540) } });
  s1.addImage({ path: I.logoBrinksBlanco, x: p(28), y: p(34), w: p(112), h: p(19.5) });
  s1.addText("Indicador de transacciones y montos", { x: p(30), y: p(222), w: p(680), h: p(50), fontFace: FUENTE, fontSize: 26, color: "FFFFFF" });
  s1.addShape("line", { x: p(30), y: p(270), w: p(510), h: 0, line: { color: AMARILLO, width: 2 } });
  s1.addText(`${mes} frente a ${etiquetaDePeriodo(reporte, "anterior")}`, { x: p(30), y: p(280), w: p(500), h: p(26), fontFace: FUENTE, fontSize: 13, color: "BFBFBF" });
  s1.addText(`©${anio} BRINK'S. CONFIDENTIAL. FOR INTERNAL USE ONLY – DO NOT DUPLICATE OR DISTRIBUTE.`, { x: p(62), y: p(505), w: p(480), h: p(16), fontFace: FUENTE, fontSize: 7, color: "BFBFBF" });

  // 4. Consolidated volumes: the months, one line per year, every point written on it.
  const porAnio = transaccionesPorAnio(reporte.monthly, reporte.month);
  const consolidado = contenido("Volúmenes Consolidado", { subtitulo: "Número de transacciones por mes" });
  marcoDeGrafica(consolidado, { x: p(28), y: p(100), w: p(877), h: p(360) });
  if (porAnio.anios.length) {
    const tonos = [AZUL_CLARO, AZUL_SERIE, AMARILLO];
    consolidado.addChart(pptx.ChartType.line, porAnio.anios.map((a) => ({
      name: a, labels: porAnio.puntos.map((pt) => pt.etiqueta), values: porAnio.puntos.map((pt) => (pt[a] === undefined ? null : pt[a])),
    })), {
      x: p(40), y: p(165), w: p(700), h: p(285), lineSize: 2.5, lineDataSymbol: "circle", lineDataSymbolSize: 7,
      chartColors: porAnio.anios.map((a, i) => tonos[Math.max(0, tonos.length - porAnio.anios.length + i)]),
      showValue: true, dataLabelPosition: "t", dataLabelFontSize: 8, dataLabelColor: TINTA, dataLabelFormatCode: "#,##0",
      showLegend: true, legendPos: "b", legendFontSize: 10, valAxisHidden: true, valGridLine: { style: "none" }, catAxisLabelFontSize: 10,
    });
  }
  consolidado.addShape("rect", { x: p(752), y: p(225), w: p(140), h: p(80), fill: { color: "FFE699" }, line: { color: AMARILLO, width: 1 } });
  consolidado.addText(
    porAnio.comparacion
      ? `Año ${porAnio.comparacion.previo} vs ${porAnio.comparacion.anio}\nDiferencia: ${cuenta(porAnio.comparacion.diferencia)}\nVariación: ${porcentaje(porAnio.comparacion.variacion)}`
      : `Año ${String(reporte.month).slice(0, 4)}\n${cuenta(indicadoresDelMes(visibles).tx.actual)} TX en ${actCorto}`,
    { x: p(752), y: p(225), w: p(140), h: p(80), fontFace: FUENTE, fontSize: 11, bold: true, color: TINTA, align: "center", valign: "middle" }
  );

  // 5. The month's volume per day.
  const dias = tendenciaDiaria(reporte.daily);
  const volumen = contenido(`Volúmenes ${mes}`, { subtitulo: "Tendencia del volumen: transacciones por día" });
  marcoDeGrafica(volumen, { x: p(20), y: p(100), w: p(915), h: p(330), curva: true });
  if (dias.length) {
    volumen.addChart(pptx.ChartType.line, [{ name: actCorto, labels: dias.map((d) => String(d.dia)), values: dias.map((d) => d.tx) }], {
      x: p(30), y: p(140), w: p(895), h: p(280), lineSize: 2.5, lineDataSymbol: "circle", lineDataSymbolSize: 7, chartColors: [AZUL_SERIE],
      showValue: true, dataLabelPosition: "t", dataLabelFontSize: 8.5, dataLabelColor: TINTA, dataLabelFormatCode: "#,##0",
      valAxisHidden: true, valGridLine: { style: "none" }, catAxisLabelFontSize: 10, showLegend: false,
    });
  }

  // 6. The month's amounts per day, in millions.
  const montos = contenido(`Montos ${mes}`, { subtitulo: "Tendencia de montos por día, en millones de pesos" });
  marcoDeGrafica(montos, { x: p(20), y: p(100), w: p(915), h: p(330), curva: true });
  if (dias.length) {
    montos.addChart(pptx.ChartType.bar, [{ name: actCorto, labels: dias.map((d) => String(d.dia)), values: dias.map((d) => millones(d.valor)) }], {
      x: p(30), y: p(140), w: p(895), h: p(280), barDir: "col", chartColors: [AZUL_SERIE], barGapWidthPct: 55,
      showValue: true, dataLabelPosition: "outEnd", dataLabelFontSize: 8, dataLabelColor: TINTA, dataLabelFormatCode: "\"$\"#,##0",
      valAxisLabelFormatCode: "\"$\"#,##0", valAxisLabelFontSize: 8, showValAxisTitle: true, valAxisTitle: "Millones", valAxisTitleFontSize: 8,
      valGridLine: { color: "E7E6E6", size: 0.5 }, catAxisLabelFontSize: 10, showLegend: false,
    });
  }

  // 7. Volume and amounts per office, with the traffic light.
  porOficina("tx", "Volúmenes por Oficina", cuenta);
  porOficina("valor", "Montos por Oficina", pesos);

  // 8. Excesses.
  seccion({ titulo: "Excesos", foto: I.seccionExcesos, icono: I.iconoExcesos });
  const excesos = contenido("Excesos");
  comparativo(excesos, "excesos", "excesos", { x: p(85), y: p(66), w: p(765), h: p(206) });
  rankingsLadoALado(excesos, { cantidad: "excesos", valor: "excesoValor", columnaCantidad: "Excesos", titulo: "Ranking de Excesos por Oficina", y: 282 });

  const sobre = contenido(`Ranking Oficinas que Sobrepasan el ${tope}%`);
  ["anterior", "actual"].forEach((periodo, j) => {
    sobre.addText(mayuscula(etiquetaDePeriodo(reporte, periodo, { corta: true })), {
      x: p(j ? 470 : 14), y: p(122), w: p(j ? 466 : 436), h: p(26), fontFace: FUENTE, fontSize: 14, color: TINTA, align: "center",
    });
  });
  rankingsLadoALado(sobre, {
    cantidad: "sobreLimite", valor: "sobreLimiteValor", columnaCantidad: `Días > ${tope}%`, titulo: `Ranking. Oficinas que sobrepasa el ${tope}% de Cupo`, y: 166, conRegional: false,
  });

  // 9. Reversals.
  seccion({ titulo: "Reversos", foto: I.seccionReversos, icono: I.iconoReversos, fotoALaDerecha: false });
  const reversos = contenido("Reversos");
  comparativo(reversos, "reversos", "reversos", { x: p(72), y: p(66), w: p(700), h: p(190) });
  rankingsLadoALado(reversos, { cantidad: "reversos", valor: "reversosValor", columnaCantidad: "Reversos", titulo: "Ranking de Reversos por Oficina", limite: 12, y: 264 });

  // 10. PQRs and commitments: not in Bantotal — the slides the committee fills in.
  seccion({ titulo: "Indicador de PQRS", foto: I.seccionPqrs, icono: I.iconoPqrs });
  const pqrs = contenido("Indicador de PQR'S", { subtitulo: "Para diligenciar en el comité: los casos no vienen en los reportes de Bantotal" });
  pqrs.addTable([
    [encabezado("Mes", "left"), encabezado("Cumple"), encabezado("No cumple"), encabezado("Total general")],
    ...Array.from({ length: 5 }, () => [celda(""), celda(""), celda(""), celda("")]),
    [encabezado("Total general", "left"), encabezado(""), encabezado(""), encabezado("")],
  ], { x: p(180), y: p(150), w: p(600), rowH: p(26), border: { type: "solid", color: "BFBFBF", pt: 0.5 } });

  const compromisos = pptx.addSlide();
  compromisos.background = { color: "FCFCFC" };
  compromisos.addImage({ path: I.degradado, x: 0, y: 0, w: p(480), h: p(540), sizing: { type: "cover", w: p(480), h: p(540) } });
  compromisos.addImage({ path: I.bandaSuperior, x: p(480), y: 0, w: p(480), h: p(11) });
  compromisos.addImage({ path: I.logoBrinksAzul, x: p(792), y: p(38), w: p(113), h: p(19.5) });
  compromisos.addText("Seguimiento a\ncompromisos", { x: p(34), y: p(30), w: p(420), h: p(80), fontFace: FUENTE, fontSize: 28, color: "FFFFFF", valign: "top" });
  compromisos.addText("Compromisos última sesión", { x: p(528), y: p(136), w: p(400), h: p(34), fontFace: FUENTE, fontSize: 20, color: AZUL_CLARO });
  compromisos.addText("•  ", { x: p(528), y: p(186), w: p(400), h: p(260), fontFace: FUENTE, fontSize: 12, color: TINTA, valign: "top" });
  reglaAmarilla(compromisos, 480, 480);
  pie(compromisos, anio, "E5E7EB");

  // 11. Thanks.
  const fin = pptx.addSlide();
  fin.background = { color: "FCFCFC" };
  fin.addImage({ path: I.degradado, x: 0, y: p(11), w: p(456), h: p(266), sizing: { type: "cover", w: p(456), h: p(266) } });
  fin.addImage({ path: I.gracias, x: p(456), y: p(11), w: p(504), h: p(266), sizing: { type: "cover", w: p(504), h: p(266) } });
  fin.addImage({ path: I.bandaSuperior, x: 0, y: 0, w: p(960), h: p(11) });
  fin.addImage({ path: I.logoBrinksAzul, x: p(792), y: p(38), w: p(113), h: p(19.5) });
  fin.addText("GRACIAS", { x: p(34), y: p(30), w: p(300), h: p(46), fontFace: FUENTE, fontSize: 28, color: "FFFFFF" });
  fin.addImage({ path: I.logoEpago, x: p(385), y: p(320), w: p(235), h: p(100), sizing: { type: "contain", w: p(235), h: p(100) } });
  reglaAmarilla(fin);
  pie(fin, anio);
  return pptx;
}

// An image as a data: URI, so the file never depends on a later request reaching the server.
export async function comoDatos(url) {
  const blob = await (await fetch(url)).blob();
  return new Promise((ok, mal) => {
    const lector = new FileReader();
    lector.onload = () => ok(lector.result);
    lector.onerror = mal;
    lector.readAsDataURL(blob);
  });
}

/** Builds and saves the presentation. pptxgenjs and the deck's images are loaded here, on the click. */
export async function descargarPresentacion(reporte, opciones) {
  const [{ default: PptxGenJS }, imagenes] = await Promise.all([
    import("pptxgenjs"),
    Promise.all(Object.entries(IMAGENES).map(async ([clave, url]) => [clave, await comoDatos(url)])).then(Object.fromEntries),
  ]);
  const pptx = presentacionDelComite(PptxGenJS, reporte, { ...opciones, imagenes });
  const periodo = reporte.wholeMonth === false ? `${reporte.from} a ${reporte.to}` : reporte.month;
  await pptx.writeFile({ fileName: `Comite de seguimiento ${opciones.cliente} ${periodo}.pptx` });
}
